import {
  Box,
  Button,
  CloseButton,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react";
import GCPImage from "components/GCPImage";
import { format, isAfter, isBefore, isSameDay } from "date-fns";
import { useSession } from "hooks/useSession";
import { getNewStartDays } from "lib/common";
import toastErrorAndReport from "lib/toastErrorAndReport";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { Calendar, DateObject } from "react-multi-date-picker";
import appStore from "store";

const CalendarContent = ({ selectedDays, setSelectedDays }) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const { locale } = router;
  const { isLoading } = useSession();

  const { tempSelectedCarIds, configs, latestOrderTimeUnion, timeOptsUnion } =
    appStore;

  const latest_order_time = useMemo(() => {
    if (tempSelectedCarIds.length === 0) return 0;
    const configList = configs.filter((config) =>
      tempSelectedCarIds.some((id) => config.car_ids.includes(id))
    );
    const minLatestOrderTime = configList.reduce((prev, cur) => {
      return prev.latest_order_time < cur.latest_order_time ? prev : cur;
    }).latest_order_time;

    return minLatestOrderTime / 60;
  }, [configs, tempSelectedCarIds]);

  const minDate = useMemo(() => {
    const now = new DateObject();

    const isAfter20 =
      now.valueOf() >
      new DateObject().set({ hour: 20, minute: 0, second: 0 }).valueOf();

    if (isAfter20) {
      return new DateObject().add(1, "days");
    } else {
      return now;
    }
  }, []);

  const mapDays = useMemo(() => {
    const disableDate = ({ date }) => {
      // 處理過年期間，時間在 2026/02/14 到 2026/02/22 之間的話，全部disable
      const isBetween =
        isBefore(new Date(date.format()), new Date("2026/02/23")) &&
        isAfter(new Date(date.format()), new Date("2026/02/13"));
      if (isBetween) {
        return {
          disabled: true,
          style: { color: "#ccc" },
        };
      }
    };
    return disableDate;
  }, []);

  // 如果選擇的日期是今天，且現在時間在 20:00 之前，設定 selectedStartClock 為現在的下一個時段
  useEffect(() => {
    if (selectedDays.length !== 0) {
      // 如果開始日期是今天
      const now = new DateObject();
      if (selectedDays[0].format("YYYY-MM-DD") === now.format("YYYY-MM-DD")) {
        // 如果現在時間在 20:00 之前，設定 selectedStartClock 為現在的下一個時段
        if (now.format("HH") < "20") {
          const nextTimeOpt = `${timeOptsUnion.find(
            (timeOpt) => timeOpt > now.format("HH")
          )}:00`;
          appStore.selectedStartClock = nextTimeOpt;
          appStore.selectedEndClock = nextTimeOpt;
          appStore.tempSelectedStartClock = nextTimeOpt;
          appStore.tempSelectedEndClock = nextTimeOpt;
        }
      } else {
        appStore.selectedStartClock = "09:00";
        appStore.selectedEndClock = "09:00";
        appStore.tempSelectedStartClock = "09:00";
        appStore.tempSelectedEndClock = "09:00";
      }
    }
  }, [selectedDays, timeOptsUnion]);

  const isMdUp = useBreakpointValue({ base: false, md: true });
  const isTw = locale === "zh-TW";

  const calendarImgPath = useMemo(() => {
    if (!router.isReady || isMdUp === undefined || isMdUp === null || isLoading)
      return "";

    const isAfter0116 = isAfter(new Date(), new Date("2025-01-16 00:00:00"));

    return isMdUp
      ? isTw
        ? `/images/lnyVersion/2025/calendarInfo_mobile${isAfter0116 ? "_0116" : "_1225"}.png`
        : `/images/lnyVersion/2025/calendarInfoEN_mobile${isAfter0116 ? "_0116" : "_1225"}.png`
      : isTw
        ? `/images/lnyVersion/2025/calendarInfo_mobile${isAfter0116 ? "_0116" : "_1225"}.png`
        : `/images/lnyVersion/2025/calendarInfoEN_mobile${isAfter0116 ? "_0116" : "_1225"}.png`;
  }, [isMdUp, isLoading, router.isReady]);

  // const isAfterNewYear = isAfter(new Date(), new Date("2025/02/02 23:59:59"));

  return (
    <Box>
      <Text fontSize="32px" fontWeight="700" lineHeight="40px" mb="24px">
        {t("choose")}
        {selectedDays && selectedDays.length === 1
          ? t("returnTime")
          : t("date")}
      </Text>
      <Calendar
        mapDays={mapDays}
        range={true}
        numberOfMonths={1}
        minDate={minDate}
        currentDate={minDate}
        value={selectedDays}
        onChange={(e) => {
          if (e.length === 2) {
            if (
              isBefore(new Date(e[0].format()), new Date("2025/01/24")) &&
              isAfter(new Date(e[1].format()), new Date("2025/01/23"))
            ) {
              toastErrorAndReport(t("Calendar_over_newyear"));
              const startDate = new DateObject(e[0]);
              if (isSameDay(new Date(e[0].format()), new Date("2025/01/23"))) {
                setSelectedDays([startDate.subtract(1, "days"), e[0]]);
                return;
              } else {
                setSelectedDays([e[0], startDate.add(1, "days")]);
                return;
              }
            } else {
              setSelectedDays(e);
            }
          } else {
            setSelectedDays(e);
          }
        }}
        maxDate={new DateObject().add(3, "months")}
      />

      {/* {!isAfterNewYear && (
        <Box
          mt="16px"
          w="100%"
          cursor="pointer"
          onClick={() => {
            router.push({
              pathname: "/newYear",
              query: router.query,
            });
          }}
        >
          <GCPImage
            src={`${calendarImgPath}?v=${new Date().getTime()}`}
            alt="timeSelect"
          />
        </Box>
      )} */}
    </Box>
  );
};

const HomeHeroTimeSelectBox = ({ values, setValues, ...props }) => {
  const { t } = useTranslation("common");
  const isMdUp = useBreakpointValue({ base: false, md: true });
  const [tempSelectedDays, setTempSelectedDays] = useState([]);

  const {
    isOpen: isOpenCalendarPopover,
    onClose: onCloseCalendarPopover,
    onOpen: onOpenCalendarPopover,
  } = useDisclosure();

  const {
    isOpen: isOpenCalendarModal,
    onClose: onCloseCalendarModal,
    onOpen: onOpenCalendarModal,
  } = useDisclosure();

  useEffect(() => {
    if (values.length === 0) {
      setValues(getNewStartDays());
    }
  }, []);

  const submitSelectedDays = () => {
    if (isMdUp) {
      if (values.length === 1) {
        const start = new DateObject(values[0]);
        const end = start.add(2, "days");
        if (
          isBefore(new Date(end.format()), new Date("2025/02/03")) &&
          isAfter(new Date(end.format()), new Date("2025/01/21"))
        ) {
          const newStartDays = getNewStartDays();
          setValues(newStartDays);
        } else {
          setValues([values[0], end]);
        }
      }
      onCloseCalendarPopover();
    } else {
      if (tempSelectedDays.length === 1) {
        // 設定結束時間為開始時間 + 2天
        const start = new DateObject(tempSelectedDays[0]);
        const end = start.add(2, "days");
        setValues([tempSelectedDays[0], end]);
        setTempSelectedDays([tempSelectedDays[0], end]);
      } else {
        setValues(tempSelectedDays);
      }
      onCloseCalendarModal();
    }
  };

  return (
    <Box flex="1" {...props}>
      <Flex>
        <Modal
          isOpen={isOpenCalendarModal}
          onClose={onCloseCalendarModal}
          size="full"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <CloseButton
                variant="ghost"
                size="lg"
                onClick={onCloseCalendarModal}
                ml="-8px"
              />
            </ModalHeader>
            <ModalBody>
              <CalendarContent
                selectedDays={tempSelectedDays}
                setSelectedDays={setTempSelectedDays}
              />
              <Button
                mt="24px"
                w="100%"
                color="white"
                bg="rgba(51, 116, 246, 1)"
                onClick={submitSelectedDays}
              >
                {t("complete")}
              </Button>
            </ModalBody>
          </ModalContent>
        </Modal>
        <Box flex="1">
          <Popover
            isOpen={isOpenCalendarPopover}
            zIndex="9999"
            placement="bottom-start"
            onClose={submitSelectedDays}
          >
            <PopoverTrigger>
              <Box px="8px" py="4px">
                <Flex
                  py="6px"
                  cursor="pointer"
                  alignItems="center"
                  justify="space-between"
                  onClick={() => {
                    if (isMdUp) {
                      onOpenCalendarPopover();
                    } else {
                      onOpenCalendarModal();
                    }
                  }}
                >
                  <Text>
                    {values[0]?.format("MM/DD")} - {values[1]?.format("MM/DD")}
                  </Text>
                </Flex>
              </Box>
            </PopoverTrigger>
            {isMdUp && (
              <PopoverContent bg="white" flex="1" width="400px" zIndex="9999">
                <PopoverArrow />
                <PopoverBody p="24px">
                  <CalendarContent
                    selectedDays={values}
                    setSelectedDays={setValues}
                  />
                </PopoverBody>
              </PopoverContent>
            )}
          </Popover>
        </Box>
      </Flex>
    </Box>
  );
};
export default HomeHeroTimeSelectBox;
